// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/ielts_speaking_coach/{questions|feedback|chat}
// with the customer's Oriora session token. The server holds the prompts + bills the
// Oriora wallet (Managed AI / BYOK). Practice history stays local; per call only the
// setup fields (exam/part/topic) OR the part's questions + the user's OWN transcript
// text leave the device. AUDIO NEVER LEAVES THE DEVICE — the examiner voice is device
// TTS, the candidate's voice is device dictation; only TEXT reaches this file.
//
// ONE billed AI call per user action. CRUD, not agentic: scoped JSON in, JSON out.
// No agent loop, no 2nd call, no tool-use chain, no direct OpenAI/Anthropic SDK here.
//
// HONESTY FENCE (mirrored in the server prompts + visible in the UI):
//   • Feedback covers Fluency & Coherence, Lexical Resource, and Grammatical Range &
//     Accuracy from the TRANSCRIPT ONLY. Pronunciation needs audio analysis and is
//     EXPLICITLY NOT scored — the app says so next to every result.
//   • Results are an ESTIMATED BAND RANGE, never a guaranteed or official score.
//   • No admission / visa / immigration-outcome claims.
//   • The transcript is fenced as DATA server-side (prompt-injection fence).

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { Exam, Part, SpeakingFeedback } from "./store";

const SERVER = "https://api.orioralabs.com";
const APP_SLUG = "ielts_speaking_coach";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep practicing.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Heartbeat endpoints (/questions, /feedback) lock the HTTP status to 200 once keepalive
    // bytes flush and encode failures IN the body via `_status` — same contract as
    // workout_tracker /draft_program. Leading whitespace IS the heartbeat: trim, parse, then
    // map failures back to the same BrainError classes the status switch above produces.
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    const text = (await res.text()).trim();
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(text) as Record<string, unknown>; } catch { throw new BrainError("unavailable", "AI is busy — please try again."); }
    const bodyStatus = typeof parsed._status === "number" ? parsed._status : undefined;
    if (bodyStatus === 402) throw new BrainError("insufficient_funds", String(parsed.message ?? "Add funds to keep practicing."));
    if (bodyStatus === 403) throw new BrainError("managed_ai_blocked", String(parsed.message ?? "Connect a vendor key in Oriora settings to use AI for this app."));
    if (bodyStatus !== undefined) throw new BrainError("unavailable", String(parsed.message ?? parsed.error ?? "AI request failed."));
    return parsed as unknown as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface CueCard {
  /** The cue-card topic line, e.g. "Describe a journey you remember well." */
  topic: string;
  /** The "You should say:" bullets. */
  bullets: string[];
}

export interface QuestionSet {
  /** Examiner questions (p1 / p3 / interview). Empty for p2. */
  questions: string[];
  /** The Part 2 cue card. null for other parts. */
  cue_card: CueCard | null;
}

/**
 * AI call 1 — the examiner's questions for ONE part (ONE billed call per "start part"
 * action). Sends only exam/part/topic; returns the question set (or the Part 2 cue card).
 * Endpoint: POST /api/apps/ielts_speaking_coach/questions.
 */
export async function getQuestions(exam: Exam, part: Exclude<Part, "repeat">, topic?: string): Promise<QuestionSet> {
  const r = await brainCall<{ questions?: unknown; cue_card?: unknown }>("questions", {
    exam,
    part,
    topic: (topic ?? "").trim(),
  });
  const questions = Array.isArray(r.questions)
    ? r.questions.map((q) => String(q).trim()).filter(Boolean)
    : [];
  let cue: CueCard | null = null;
  if (r.cue_card && typeof r.cue_card === "object") {
    const c = r.cue_card as { topic?: unknown; bullets?: unknown };
    const topicLine = String(c.topic ?? "").trim();
    const bullets = Array.isArray(c.bullets) ? c.bullets.map((b) => String(b).trim()).filter(Boolean) : [];
    if (topicLine) cue = { topic: topicLine, bullets };
  }
  if (questions.length === 0 && !cue) {
    throw new BrainError("unavailable", "No questions came back — please try again.");
  }
  return { questions, cue_card: cue };
}

/**
 * AI call 2 — band-style feedback on ONE part's transcript (ONE billed call per
 * "Get feedback"). Sends the part, the questions asked, and the user's own transcript.
 * Returns per-criterion notes (Fluency & Coherence / Lexical Resource / Grammatical
 * Range & Accuracy) + an ESTIMATED band range. Pronunciation is NOT scored (transcript
 * has no audio) — the server refuses to invent it and the UI says so.
 * Endpoint: POST /api/apps/ielts_speaking_coach/feedback.
 */
export async function getFeedback(
  part: Exclude<Part, "repeat">,
  transcript: string,
  opts?: { exam?: Exam; topic?: string; questions?: string[] },
): Promise<SpeakingFeedback> {
  const t = transcript.trim();
  if (t.length < 40) {
    throw new BrainError("unavailable", "Say (or type) a bit more first — feedback needs at least a few sentences.");
  }
  const r = await brainCall<Partial<SpeakingFeedback>>("feedback", {
    part,
    transcript: t,
    exam: opts?.exam ?? "ielts",
    topic: (opts?.topic ?? "").trim(),
    questions: (opts?.questions ?? []).slice(0, 10),
  });
  const crit = (c: unknown) => ({
    band: Number((c as { band?: unknown })?.band ?? 0) || 0,
    note: String((c as { note?: unknown })?.note ?? "").trim(),
  });
  const fb: SpeakingFeedback = {
    fluency_coherence: crit(r.fluency_coherence),
    lexical_resource: crit(r.lexical_resource),
    grammar: crit(r.grammar),
    band_low: Number(r.band_low ?? 0) || 0,
    band_high: Number(r.band_high ?? 0) || 0,
    strengths: Array.isArray(r.strengths) ? r.strengths.map(String).filter(Boolean) : [],
    improvements: Array.isArray(r.improvements) ? r.improvements.map(String).filter(Boolean) : [],
  };
  if (fb.band_low <= 0 || fb.band_high <= 0) {
    throw new BrainError("unavailable", "Feedback didn't come back cleanly — please try again.");
  }
  return fb;
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds the FAITHFUL backup locally (export_backup); these calls relay it to the
// user's OWN connected cloud store — their GitHub repo or Google Drive (connected once in the
// Oriora account/Connections hub; the server picks whichever). Oriora RETAINS nothing. Plain
// HTTP → works on iOS with no file picker. Endpoints: POST/GET /api/apps/ielts_speaking_coach/backup.
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
// ── Accent Lab: one shadowing attempt → prosody-first coaching (billed; transcript+timing only,
// never audio — the same honesty fence as /feedback). ──
export interface ShadowFeedback {
  one_thing: string;
  prosody: string;
  segmentals: string;
  praise: string;
}

export async function getShadowFeedback(input: {
  sentence: string; heard: string; l1: string; lang: "en" | "zh"; modelMs: number; speakerMs: number;
}): Promise<ShadowFeedback> {
  const r = await brainCall<Partial<ShadowFeedback>>("shadow-feedback", {
    sentence: input.sentence, heard: input.heard, l1: input.l1, lang: input.lang,
    model_ms: input.modelMs, speaker_ms: input.speakerMs,
  });
  return {
    one_thing: String(r.one_thing ?? ""),
    prosody: String(r.prosody ?? ""),
    segmentals: String(r.segmentals ?? ""),
    praise: String(r.praise ?? ""),
  };
}

export interface PlanSession {
  idx: number;
  title: string;
  drill_keys: string[];
  minutes: number;
  note?: string;
  done_at?: string;   // stamped locally when the user marks the session done
}

export async function draftPracticePlan(input: {
  goal: { name: string; lang: string; target_date: string; months_left: number | null };
  history: { days_practiced_last_14: number; attempts_by_drill: Record<string, number>; benchmarks: number };
  minutesPerDay: number;
  catalog: { key: string; title: string }[];
  ownWords: string;
}): Promise<{ name: string; sessions: PlanSession[] }> {
  const r = await brainCall<{ name?: unknown; sessions?: unknown }>("practice-plan", {
    goal: input.goal, history: input.history, minutes_per_day: input.minutesPerDay,
    catalog: input.catalog, own_words: input.ownWords,
  });
  const sessions = (Array.isArray(r.sessions) ? r.sessions : [])
    .map((x: any, i: number) => ({
      idx: i + 1, // sequence position IS the identity — model-supplied idx can repeat and would mark the wrong session done
      title: String(x?.title ?? `Session ${i + 1}`).slice(0, 120),
      drill_keys: Array.isArray(x?.drill_keys) ? x.drill_keys.map(String) : [],
      minutes: Math.max(10, Math.min(60, Number(x?.minutes) || 25)),
      note: typeof x?.note === "string" ? x.note.slice(0, 300) : undefined,
    }))
    .filter((x: PlanSession) => x.drill_keys.length > 0);
  if (!sessions.length) throw new BrainError("unavailable", "The draft came back empty — try again.");
  return { name: String(r.name ?? "Practice plan").slice(0, 80), sessions };
}

export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `ielts-speaking-coach` as a return target (RETURN_APPS in
// web/landing/app/auth/callback/page.tsx — operator task, then `vercel --prod`).
export const openWebLogin = () => invoke<void>("open_login_window");
